import React, { memo, useMemo, useRef } from "react";
import { useImmer } from "use-immer";

import {
  checkoutUpsellConversionGraphConfig,
  postPurchaseConversionGraphConfig,
} from "@intelligems/ig-graphs/graph-configs";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { CustomEventDto } from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { VariationHeaderDropdown } from "@src/features/analytics/baseDashboard/VariationHeaderDropdown";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { MetricDetailSection } from "@src/features/analytics/metricDetails/MetricDetailSection";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import { ConversionFunnelTable } from "@src/features/analytics/tables/ConversionFunnelTable";
import { getGridWidthCalc } from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { getHasAnyUpsellBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { getControlVariationId } from "@src/utils/controlVariation";
import { UseQueryResult } from "@tanstack/react-query";

interface ConversionAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const ConversionAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: ConversionAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const hasUpsellBlocks = useSelector(getHasAnyUpsellBlocks());
    const hasPostPurchaseUpsell = experience.testTypes.hasTestPostPurchase;
    const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const [selectedVariationId, setSelectedVariationId] = useImmer(
      dashboardFilters?.compareGroupB || getControlVariationId(variations)
    );
    const [hover, setHover] = useImmer(-1);

    const isDataLoading = useMemo(() => {
      return data[0].isLoading || data[0].isPending;
    }, [data[0].isLoading, data[0].isPending]);

    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        <Grid>
          <BaseMetricSection
            groupTitle={"Conversion Metrics"}
            showCompareGroups={true}
          >
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(0);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  totalTestVisitors={totalExperienceVisitors}
                  config={graphConfigs[0]}
                  renderLocation={"experiment"}
                  testDuration={testDuration}
                  isDataLoading={isDataLoading}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[0],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(1);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[1]}
                  isDataLoading={isDataLoading}
                  totalTestVisitors={totalExperienceVisitors}
                  renderLocation={"experiment"}
                  testDuration={testDuration}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[1],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(2);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[2]}
                  isDataLoading={isDataLoading}
                  renderLocation={"experiment"}
                  testDuration={testDuration}
                  totalTestVisitors={totalExperienceVisitors}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[2],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(3);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[3]}
                  renderLocation={"experiment"}
                  totalTestVisitors={totalExperienceVisitors}
                  testDuration={testDuration}
                  isDataLoading={isDataLoading}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[3],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>

            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(4);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[4]}
                  totalTestVisitors={totalExperienceVisitors}
                  testDuration={testDuration}
                  isDataLoading={isDataLoading}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[4],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(4);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[5]}
                  totalTestVisitors={totalExperienceVisitors}
                  testDuration={testDuration}
                  isDataLoading={isDataLoading}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[5],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            <Grid size={gridWidth}>
              <BaseGraphPaper
                onMouseEnter={() => {
                  setHover(4);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                <GraphsWrapper
                  config={graphConfigs[6]}
                  totalTestVisitors={totalExperienceVisitors}
                  testDuration={testDuration}
                  isDataLoading={isDataLoading}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[6],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              </BaseGraphPaper>
            </Grid>
            {hasUpsellBlocks && (
              <Grid size={gridWidth}>
                <BaseGraphPaper
                  onMouseEnter={() => {
                    setHover(7);
                  }}
                  onMouseLeave={() => {
                    setHover(-1);
                  }}
                >
                  <GraphsWrapper
                    config={checkoutUpsellConversionGraphConfig[0]}
                    totalTestVisitors={totalExperienceVisitors}
                    testDuration={testDuration}
                    isDataLoading={isDataLoading}
                    renderLocation={"experiment"}
                    data={selectData({
                      dashboardData: data,
                      config: checkoutUpsellConversionGraphConfig[0],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                  />
                </BaseGraphPaper>
              </Grid>
            )}
            {hasPostPurchaseUpsell && (
              <Grid size={gridWidth}>
                <BaseGraphPaper
                  onMouseEnter={() => {
                    setHover(hasUpsellBlocks ? 8 : 7);
                  }}
                  onMouseLeave={() => {
                    setHover(-1);
                  }}
                >
                  <GraphsWrapper
                    config={postPurchaseConversionGraphConfig[0]}
                    totalTestVisitors={totalExperienceVisitors}
                    testDuration={testDuration}
                    isDataLoading={isDataLoading}
                    renderLocation={"experiment"}
                    data={selectData({
                      dashboardData: data,
                      config: postPurchaseConversionGraphConfig[0],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                  />
                </BaseGraphPaper>
              </Grid>
            )}
          </BaseMetricSection>
        </Grid>
        <Stack direction={"column"} sx={{ width: "100%" }}>
          <BaseMetricSection
            headerDropdown={
              <VariationHeaderDropdown
                selectedVariationId={selectedVariationId}
                setSelectedVariationId={setSelectedVariationId}
              />
            }
            groupTitle={"Conversion Funnel"}
          >
            <Grid sx={{ maxWidth: "1200px" }} size={12}>
              {data[0].isLoading ? (
                <Grid sx={{ width: "1100px" }}>Loading Data ...</Grid>
              ) : (
                <GraphsWrapper
                  isDataLoading={isDataLoading}
                  focusedVariationId={selectedVariationId}
                  config={graphConfigs[7]}
                  totalTestVisitors={totalExperienceVisitors}
                  testDuration={testDuration}
                  renderLocation={"experiment"}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[7],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex: 0,
                    variations,
                  })}
                  rawData={data}
                  compareVariationId={compareVariationId}
                />
              )}
            </Grid>
            <Grid size={12}>
              <MetricDetailSection
                title={"Conversion Funnel Details"}
                metricDetails={{
                  isPrimary: false,
                }}
              >
                <ConversionFunnelTable
                  loading={data[0].isLoading || data[0].isPending}
                  variationOverviewData={data}
                />
              </MetricDetailSection>
            </Grid>
          </BaseMetricSection>
        </Stack>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={graphConfigs}
            dropdownOptions={graphConfigs.slice(0, 5).map((config) => ({
              title: config.title[0],
              id: config.metrics[0],
            }))}
            variationOverviewData={data}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            testDuration={testDuration}
            dashboardData={data}
            graphConfigs={graphConfigs.filter((config) => config.hasAudience)}
          />
        </Grid>
      </Stack>
    );
  }
);
